import { faChevronRight } from "@fortawesome/pro-regular-svg-icons";
import { Link, useLocation } from "react-router-dom";

import { AwsAccountsSettings } from "src/adminPanel/AwsAccountsSettings";
import { ModulesSettings } from "src/adminPanel/modules/ModulesSettings";
import { ModulesV2Settings } from "src/adminPanel/modulesV2/ModulesV2Settings";
import { OrganizationsWorkspacesSettings } from "src/adminPanel/OrganizationsWorkspacesSettings";
import {
  useAllAWSAccounts,
  useAllOrgs,
  useAllUsers,
} from "src/adminPanel/queries";
import { RoleBasedUsers } from "src/adminPanel/RoleBasedUsers";
import { UserSettings } from "src/adminPanel/UserSettings";
import { useUserOrganizations, useWorkspaces } from "src/api/queries";
import TaktileLogo from "src/assets/Taktile.svg?react";
import { LoadingView } from "src/base-components/LoadingView";
import { Tabs } from "src/base-components/Tabs";
import { AWSAccount, WorkspaceDataplane } from "src/clients/flow-api";
import { OrganizationInDB, User } from "src/clients/taktile-api";
import { Icon } from "src/design-system/Icon";
import { useDocumentTitle } from "src/hooks/useDocumentTitle";

const AdminPanelTabs: React.FC = () => {
  const workspaceData = useWorkspaces(true, true);
  const userOrganizations = useUserOrganizations();
  const allUsers = useAllUsers();
  const allOrgs = useAllOrgs();
  const allAWSAccounts = useAllAWSAccounts(true);
  useDocumentTitle(["Admin Panel"]);
  const location = useLocation();
  // Determine active tab from path
  let tabKey = "Workspaces";
  if (location.pathname.endsWith("/modules-v2")) {
    tabKey = "Modules V2";
  } else if (location.pathname.endsWith("/modules")) {
    tabKey = "Modules";
  } else if (location.pathname.endsWith("/aws-accounts")) {
    tabKey = "AWS Accounts";
  } else if (location.pathname.endsWith("/users")) {
    tabKey = "Users";
  } else if (location.pathname.endsWith("/inactive-organizations")) {
    tabKey = "Inactive Organizations";
  } else if (location.pathname.endsWith("/access-review")) {
    tabKey = "Access Review";
  }

  return (
    <>
      <div className="flex h-screen w-screen flex-col bg-gray-100">
        <header className="box-border flex min-h-[52px] w-full flex-row items-center gap-x-2.5 bg-white py-2 pr-5 pl-4">
          <Link to="..">
            <TaktileLogo />
          </Link>
          <Icon icon={faChevronRight} size="xs" />
          <p className="font-inter-semibold-13px text-gray-800">Admin Panel</p>
        </header>
        <Tabs
          containerClassName="mt-2 px-4 pb-4 flex-1 min-h-0 flex flex-col"
          panelClassName="flex-1 min-h-0 decideScrollbar"
          panelsClassName="flex-1 min-h-0 flex"
          selectedKey={tabKey}
          tabs={[
            {
              key: "Workspaces",
              label: <Link to="/admin-panel/organizations">Workspaces</Link>,
              content: (
                <LoadingView
                  queryResult={[workspaceData, userOrganizations]}
                  renderUpdated={([workspaces, organizations]: [
                    WorkspaceDataplane[],
                    OrganizationInDB[],
                  ]) => (
                    <div className="border-gray-200 border-t pt-4">
                      <OrganizationsWorkspacesSettings
                        isActive={true}
                        organizations={organizations}
                        workspaces={workspaces}
                      />
                    </div>
                  )}
                />
              ),
            },
            {
              key: "Modules",
              label: <Link to="/admin-panel/modules">Modules</Link>,
              content: <ModulesSettings />,
            },
            {
              key: "Modules V2",
              label: (
                <Link to="/admin-panel/modules-v2">
                  Modules V2 (do not use)
                </Link>
              ),
              content: <ModulesV2Settings />,
            },
            {
              key: "Users",
              label: <Link to="/admin-panel/users">Users</Link>,
              content: (
                <LoadingView
                  queryResult={[allUsers, allOrgs, workspaceData]}
                  renderUpdated={([users, orgs, wss]: [
                    User[],
                    OrganizationInDB[],
                    WorkspaceDataplane[],
                  ]) => (
                    <div className="border-gray-200 border-t pt-4">
                      <UserSettings orgs={orgs} users={users} wss={wss} />
                    </div>
                  )}
                />
              ),
            },
            {
              key: "AWS Accounts",
              label: <Link to="/admin-panel/aws-accounts">AWS Accounts</Link>,
              content: (
                <LoadingView
                  queryResult={[allAWSAccounts, workspaceData]}
                  renderUpdated={([awsAccounts, workspaces]: [
                    AWSAccount[],
                    WorkspaceDataplane[],
                  ]) => (
                    <div className="border-gray-200 border-t pt-4">
                      <AwsAccountsSettings
                        awsAccounts={awsAccounts}
                        workspaces={workspaces}
                      />
                    </div>
                  )}
                />
              ),
            },
            {
              key: "Inactive Organizations",
              label: (
                <Link to="/admin-panel/inactive-organizations">
                  Inactive Organizations
                </Link>
              ),
              content: (
                <LoadingView
                  queryResult={[workspaceData, allOrgs]}
                  renderUpdated={([workspaces, allOrgs]: [
                    WorkspaceDataplane[],
                    OrganizationInDB[],
                  ]) => (
                    <div className="border-gray-200 border-t pt-4">
                      <OrganizationsWorkspacesSettings
                        isActive={false}
                        organizations={allOrgs.filter((org) => !org.is_active)}
                        workspaces={workspaces}
                      />
                    </div>
                  )}
                />
              ),
            },
            {
              key: "Access Review",
              label: <Link to="/admin-panel/access-review">Access Review</Link>,
              content: <RoleBasedUsers />,
            },
          ]}
        />
      </div>
    </>
  );
};

export const AdminPanel: React.FC = () => {
  return (
    <>
      <AdminPanelTabs />
    </>
  );
};
